import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Output, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StockDataService } from '../../../core/services/stock-data.service';
import { LoggingService } from '../../../core/services/logging.service';
import { Company } from '../../../shared/models/company-model';
import { CompanyDisplayComponent } from '../company-display/company-display.component';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { Subject, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';

interface StockSearchEvent {
  query: string;
}

@Component({
  selector: 'app-stock-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CompanyDisplayComponent,
    AutoCompleteModule,
  ],
  templateUrl: './stock-search.component.html',
  styleUrl: './stock-search.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockSearchComponent {
  @Output() appStockSelected = new EventEmitter();
  @ViewChild(AutoComplete) autoComplete?: AutoComplete;

  selectedCompany: Company | string | null = null;
  searchResults = signal<Company[]>([]);
  isLoading = signal(false);
  private destroyRef = inject(DestroyRef);
  private logger = inject(LoggingService);
  private searchQueries = new Subject<string>();

  constructor(private stockDataService: StockDataService) {
    this.searchQueries
      .pipe(
        map((query) => query.trim()),
        distinctUntilChanged(),
        tap((query) => {
          this.searchResults.set([]);
          if (query) {
            if (this.autoComplete) {
              this.autoComplete.loading = true;
            }
          }
        }),
        switchMap((query) => {
          if (!query) {
            return of([] as Company[]);
          }

          return timer(300).pipe(
            tap(() => this.isLoading.set(true)),
            switchMap(() =>
              this.stockDataService.searchStocks(query).pipe(
                catchError((error) => {
                  this.logger.error('Search error:', error);
                  return of([] as Company[]);
                }),
                finalize(() => this.isLoading.set(false))
              )
            )
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((results) => {
        // PrimeNG's AutoComplete only renders newly-assigned suggestions while
        // its `loading` flag is true (see handleSuggestionsChange). Earlier
        // assignments in this stream (e.g. clearing stale results) consume that
        // flag, so re-arm it here to ensure the latest results are displayed.
        if (this.autoComplete) {
          this.autoComplete.loading = true;
        }
        this.searchResults.set(results);
      });
  }

  search(event: StockSearchEvent) {
    this.searchQueries.next(event.query);
  }

  onCompanySelect(company: Company) {
    this.selectedCompany = null;
    this.appStockSelected.emit(company);
  }

  onEnterKey(event: Event): void {
    const selectedCompany = this.resolveSelectedCompany();
    if (!selectedCompany) {
      return;
    }

    event.preventDefault();
    this.onCompanySelect(selectedCompany);
  }

  private resolveSelectedCompany(): Company | null {
    if (this.isCompany(this.selectedCompany)) {
      return this.selectedCompany;
    }

    const [firstSuggestion] = this.searchResults();
    return firstSuggestion ?? null;
  }

  private isCompany(value: Company | string | null): value is Company {
    return value !== null && typeof value !== 'string';
  }
}
